import { useState, useEffect, useCallback, useRef } from 'react';
import { ReviewStatus, RevisedFeedback, ReviewStatusResponse } from '@recruitai/shared';
import { apiClient } from '../lib/api-client';

interface UseReviewStatusOptions {
  jobId: string;
  candidateId: string;
  interviewId: string;
  enabled: boolean;
}

interface UseReviewStatusReturn {
  status: ReviewStatus | null;
  revisedFeedback: RevisedFeedback | null;
  isPolling: boolean;
  reviewNumber: number | null;
}

export function useReviewStatus({
  jobId,
  candidateId,
  interviewId,
  enabled,
}: UseReviewStatusOptions): UseReviewStatusReturn {
  const [status, setStatus] = useState<ReviewStatus | null>(null);
  const [revisedFeedback, setRevisedFeedback] = useState<RevisedFeedback | null>(null);
  const [reviewNumber, setReviewNumber] = useState<number | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const basePath = `/jobs/${jobId}/candidates/${candidateId}/interviews/${interviewId}`;

  const checkStatus = useCallback(async () => {
    if (!jobId || !candidateId || !interviewId) return;
    try {
      const response = await apiClient.get<ReviewStatusResponse>(`${basePath}/review/status`);
      setStatus(response.status);
      setRevisedFeedback(response.revisedFeedback ?? null);
      setReviewNumber(response.reviewNumber ?? null);

      if (response.status === 'completed' || response.status === 'failed') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPolling(false);
        }
      }
    } catch {
      // Silent on polling errors
    }
  }, [jobId, candidateId, interviewId, basePath]);

  useEffect(() => {
    if (enabled && !intervalRef.current) {
      setIsPolling(true);
      checkStatus();
      intervalRef.current = setInterval(checkStatus, 10000);
    }

    if (!enabled && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsPolling(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, checkStatus]);

  return { status, revisedFeedback, isPolling, reviewNumber };
}
